import { useMemo } from 'react';
import { useTracking } from '@podiumhq/tracking';

import { useBusinessInfoContext } from 'contexts/BusinessContextProvider';
import useChangePage from 'hooks/useChangePage';
import { useCreditCardContext } from 'contexts/CreditCardDetailsContextProvider';
import { useNotificationContext } from 'contexts/NotificationContextProvider';
import { useTrialsContext } from 'contexts/TrialsContextProvider';
import useCreateSetupIntentAndStripeSetupIntent from 'hooks/useCreateSetupIntentAndStripeSetupIntent';
import { getCookies } from 'lib/getCookies';
import { TRIAL_URL } from 'routes/constants';

import {
  onSuccessfulCreateTrial,
  redirectToOnboarding,
  setTrialError,
} from '../utils';

import useCreateTrial from './useCreateTrial';
import useCreateTrialNoCard from './useCreateTrialNoCard';
import useCheckResourcesReady from './useCheckResourcesReady';

const useSubmitCreateTrial = () => {
  const changePage = useChangePage();
  const { address, businessInfo = {}, gmb } = useBusinessInfoContext() || {};
  const { handleStripeAndSetupIntent, stripeNotLoaded } =
    useCreateSetupIntentAndStripeSetupIntent(true);
  const { hasValidCreditCard, zip } = useCreditCardContext();
  const [createTrial] = useCreateTrial();
  const [createTrialNoCard] = useCreateTrialNoCard();
  const {
    setShouldDisplay: shouldDisplayError,
    setCustomMessage,
    setErrorReason,
  } = useNotificationContext() || {};
  const { handleCheckResources } = useCheckResourcesReady();

  const { trackEvent } = useTracking();
  const {
    email,
    firstName,
    firstUtmMedium,
    googleClientId,
    hasValidName,
    lastName,
    partnerId,
    password,
    phone,
    salesforceCampaignId,
    setIsSubmittingTrial: setIsSubmitting,
    setSetupIntentData,
    trialPlan = {},
    utmMedium,
    showDiscountPricing,
    ssqt,
    fbclid,
    msclkid,
    googleClickId,
  } = useTrialsContext() || {};
  const { plan } = trialPlan;

  const baseVariables = useMemo(
    () => ({
      businessName: businessInfo.name,
      email,
      firstName,
      lastName,
      phone: phone || businessInfo.phone,
      postalCode: zip || businessInfo.zip,
    }),
    [
      businessInfo.name,
      businessInfo.phone,
      businessInfo.zip,
      email,
      firstName,
      lastName,
      phone,
      zip,
    ]
  );

  const setError = (error, isStripeError = false) => {
    setTrialError({
      error,
      isStripeError,
      setCustomMessage,
      setErrorReason,
      shouldDisplayError,
      trackEvent,
    });

    setIsSubmitting(false);
  };

  const handleCreateTrialResponse = ({
    errors,
    isFreeTrialStart = true,
    responseData = {},
    redirect = redirectToOnboarding,
  } = {}) => {
    if (!errors) {
      const locationUid = responseData.organization?.locations[0].uid;
      const handleRedirect = () => {
        showDiscountPricing
          ? changePage(`${TRIAL_URL}/buy-now`)
          : redirect(locationUid);
      };

      onSuccessfulCreateTrial({
        email,
        firstName,
        idToken: responseData.idToken,
        isFreeTrialStart,
        lastName,
        locationUid,
        noPasswordSet: !password,
        organizationUid: responseData.organization?.uid,
        plan,
        refreshToken: responseData.refreshToken,
        userUid: responseData.userUid,
      });

      trackEvent('trials.startTrial.success');

      handleCheckResources(
        {
          organizationUid: responseData.organization?.uid,
          locationUid,
        },
        handleRedirect
      );
    }
  };

  const createNewTrialNoCreditCard = async (
    validGmb = false,
    redirect = redirectToOnboarding
  ) => {
    let input = {
      ...(validGmb && { address }),
      businessName: baseVariables.businessName,
      email: baseVariables.email,
      firstName: baseVariables.firstName,
      firstUtmMedium,
      googleClientId,
      googlePlaceId: gmb?.placeId || null,
      lastName: baseVariables.lastName,
      partnerId,
      password,
      phone: baseVariables.phone,
      salesforceCampaignId,
      sessionId: getCookies().podiumSessionId,
      googleClickId,
      fbclid,
      msclkid,
      tosAccepted: true,
      utmMedium,
      ssqt,
    };

    try {
      const {
        data: { createAccountWithNoCreditCardTrial: trialData },
        errors,
      } = await createTrialNoCard({
        context: {
          headers: {
            'x-ipqs-id': window.ipqs,
          },
        },
        variables: { input },
      });

      handleCreateTrialResponse({ errors, responseData: trialData, redirect });
    } catch (error) {
      setError(error);
    }
  };

  const createNewTrial = async ({
    stripeCustomerId,
    stripePaymentMethodId,
    stripeSetupIntentId,
    incompleteGmb = false,
  }) => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const {
        data: { createTrial: trialData },
        errors,
      } = await createTrial({
        context: {
          headers: {
            'x-ipqs-id': window.ipqs,
          },
        },
        variables: {
          ...baseVariables,
          address: incompleteGmb ? {} : address,
          currentTrialSlug: plan,
          firstUtmMedium,
          googleClientId,
          googlePlaceId: gmb?.placeId || null,
          partnerId,
          password,
          salesforceCampaignId,
          sessionId: getCookies().podiumSessionId,
          stripeCustomerId,
          stripePaymentMethodId,
          stripeSetupIntentId,
          tosAccepted: true,
          userTimezone,
          utmMedium,
        },
      });

      handleCreateTrialResponse({
        errors,
        isFreeTrialStart: false,
        responseData: trialData,
      });
    } catch (error) {
      setError(error);
    }
  };

  const isReadyToStartSubmit = (event) => {
    trackEvent(`trials.startTrial.startTrialClicked`);
    shouldDisplayError(false);
    setIsSubmitting(true);
    // Block native form submission.
    event.preventDefault();

    if (stripeNotLoaded) {
      trackEvent(`trials.startTrial.error`, {
        error: 'Stripe not loaded',
      });
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      setIsSubmitting(false);
      return false;
    }
    return true;
  };

  const hasValidCreditCardAndName = () => {
    const validName = hasValidName();
    const validCreditCard = hasValidCreditCard();

    return validName && validCreditCard;
  };

  const handlePaymentButtonSubmit = async (event) => {
    if (!isReadyToStartSubmit(event)) {
      return;
    }

    if (!hasValidCreditCardAndName()) {
      return setIsSubmitting(false);
    }

    try {
      // CREATE SETUP INTENT AND GET SECRET
      const {
        createSetupIntentError,
        setUpIntentData,
        stripeSetUpIntent,
        stripeError,
      } = (await handleStripeAndSetupIntent(baseVariables)) || {};

      if (createSetupIntentError) {
        return setError(createSetupIntentError.message);
      }

      if (stripeError) {
        return setError(stripeError, true);
      }

      const data = {
        stripeCustomerId: setUpIntentData?.createSetupIntent?.id,
        stripePaymentMethodId: stripeSetUpIntent?.payment_method,
        stripeSetupIntentId: stripeSetUpIntent?.id,
      };

      setSetupIntentData(data);

      return data;
    } catch (error) {
      setError(error);
    }
  };

  return {
    createNewTrial,
    createNewTrialNoCreditCard,
    handlePaymentButtonSubmit,
  };
};

export default useSubmitCreateTrial;
